import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useTimezone } from '../../../../hooks/useTimezone';
import { formatInTimezone, formatDateOnly, formatTimeOnly } from '../../../../utils/timezoneUtils';
import { 
  Clock, Calendar, PlusCircle, Edit2, Trash2, AlertCircle,
  CheckCircle, X, ArrowLeft, Save, Building2, DollarSign, File, User
} from 'lucide-react';

const MyTime = () => {
  const { currentUser } = useAuth();
  const userTimezone = useTimezone();
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Form state
  const [formData, setFormData] = useState({
    businessUnit: '',
    businessUnitName: '',
    businessUnitIdentifier: '',
    isBillable: false,
    hours: '00',
    minutes: '00',
    workCompleted: '',
    project: '',
  });
  
  // Other state
  const [businessUnits, setBusinessUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [dailySummary, setDailySummary] = useState([]);
  const [topBusinessUnits, setTopBusinessUnits] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Hours and minutes options
  const hoursOptions = Array.from({ length: 13 }, (_, i) => 
    i.toString().padStart(2, '0')
  );
  
  const minutesOptions = ['00', '15', '30', '45'];

  // Fetch business units and projects
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch business units
        const buResponse = await fetch(`${API_URL}/business-units`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!buResponse.ok) {
          throw new Error('Failed to fetch business units');
        }
        
        const buData = await buResponse.json();
        setBusinessUnits(buData);
        
        // Fetch projects
        const projectsResponse = await fetch(`${API_URL}/projects`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!projectsResponse.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const projectsData = await projectsResponse.json();
        setProjects(projectsData);
        
      } catch (err) {
        setError('Failed to load data. Please try again later.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL]);

  // Fetch time entries
  useEffect(() => {
    const fetchTimeEntries = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Get entries for current user
        const response = await fetch(`${API_URL}/mytime?employee_id=${currentUser.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch time entries');
        }
        
        const data = await response.json();
        setTimeEntries(data.entries);
        
        // Fetch daily summary
        await fetchDailySummary();
        
        // Fetch business unit summary
        await fetchBusinessUnitSummary();
        
      } catch (err) {
        setError('Failed to load time entries. Please try again later.');
        console.error('Error fetching time entries:', err);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchTimeEntries();
    }
  }, [API_URL, currentUser]);

  // Fetch daily summary data
  const fetchDailySummary = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get dates for last 7 days
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 6);
      
      const response = await fetch(
        `${API_URL}/mytime/summary?employee_id=${currentUser.id}&start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate}&group_by=day`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch time summary');
      }
      
      const data = await response.json();
      
      // Transform data to match our component needs
      const summary = data.summaries.map(day => {
        let dateObj;
        try {
          if (day.period.includes('T')) {
            // Has time component - parse as is
            dateObj = new Date(day.period);
          } else {
            // Date only - parse as local date to avoid timezone shifts
            const [year, month, dayOfMonth] = day.period.split('-').map(Number);
            dateObj = new Date(year, month - 1, dayOfMonth);
          }
          // Validate date
          if (isNaN(dateObj.getTime())) {
            dateObj = new Date(); // Fallback to today
          }
        } catch (error) {
          console.warn('Error parsing date:', day.period, error);
          dateObj = new Date(); // Fallback to today
        }
        
        return {
          date: dateObj,
          totalHours: Math.floor(day.total_hours),
          totalMinutes: Math.round((day.total_hours % 1) * 60),
          formattedTotal: `${Math.floor(day.total_hours)}h ${Math.round((day.total_hours % 1) * 60)}m`
        };
      });
      
      setDailySummary(summary);
      
    } catch (err) {
      console.error('Error fetching daily summary:', err);
    }
  };

  // Fetch business unit summary
  const fetchBusinessUnitSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get dates for last 7 days
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 6);
      
      const response = await fetch(
        `${API_URL}/mytime/summary?employee_id=${currentUser.id}&start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate}&group_by=business_unit`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch business unit summary');
      }
      
      const data = await response.json();
      
      // Transform data and sort by total hours
      const buSummary = data.summaries
        .map(bu => ({
          id: bu.business_unit_id,
          name: bu.business_unit_name,
          identifier: bu.business_unit_identifier,
          totalMinutes: bu.total_hours * 60,
          totalHours: Math.floor(bu.total_hours),
          totalMinutesRemaining: Math.round((bu.total_hours % 1) * 60),
          formattedTotal: `${Math.floor(bu.total_hours)}h ${Math.round((bu.total_hours % 1) * 60)}m`
        }))
        .sort((a, b) => b.totalMinutes - a.totalMinutes)
        .slice(0, 3); // Top 3
      
      setTopBusinessUnits(buSummary);
      
    } catch (err) {
      console.error('Error fetching business unit summary:', err);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'businessUnit') {
      // When business unit changes, also update name and identifier
      const selectedBU = businessUnits.find(bu => bu.id === parseInt(value));
      setFormData({
        ...formData,
        businessUnit: value,
        businessUnitName: selectedBU ? selectedBU.name : '',
        businessUnitIdentifier: selectedBU ? selectedBU.identifier : ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.businessUnit) {
      setError('Please select a business unit');
      return;
    }
    
    if (!formData.workCompleted) {
      setError('Please describe the work completed');
      return;
    }
    
    // Get total hours as float
    const totalHours = parseFloat(formData.hours) + (parseFloat(formData.minutes) / 60);
    if (totalHours === 0) {
      setError('Please enter time greater than 0');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Create payload with local date
      // For date-only fields, we want the date as it appears in the user's timezone
      const currentDate = new Date();
      const localDateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      
      const timeEntry = {
        business_unit_id: parseInt(formData.businessUnit),
        business_unit_name: formData.businessUnitName,
        business_unit_identifier: formData.businessUnitIdentifier,
        is_billable: formData.isBillable,
        hours: parseInt(formData.hours),
        minutes: parseInt(formData.minutes),
        work_completed: formData.workCompleted,
        project: formData.project || null,
        activity: null,
        date: localDateString // Send date in user's local timezone
      };
      
      let response;
      
      if (editingEntry) {
        // Update existing entry
        response = await fetch(`${API_URL}/mytime/${editingEntry.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(timeEntry)
        });
      } else {
        // Create new entry
        response = await fetch(`${API_URL}/mytime`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(timeEntry)
        });
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save time entry');
      }
      
      // Success - refresh data
      const data = await response.json();
      
      // Refresh data
      const entriesResponse = await fetch(`${API_URL}/mytime?employee_id=${currentUser.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (entriesResponse.ok) {
        const entriesData = await entriesResponse.json();
        setTimeEntries(entriesData.entries);
      }
      
      // Refresh summaries
      await fetchDailySummary();
      await fetchBusinessUnitSummary();
      
      setSuccess(editingEntry ? 'Time entry updated successfully' : 'Time entry added successfully');
      
      // Reset form
      setFormData({
        businessUnit: '',
        businessUnitName: '',
        businessUnitIdentifier: '',
        isBillable: false,
        hours: '00',
        minutes: '00',
        workCompleted: '',
        project: '',
      });
      
      setEditingEntry(null);
      
    } catch (err) {
      setError(err.message || 'Failed to save time entry. Please try again.');
      console.error('Error saving time entry:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle entry edit
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      businessUnit: entry.business_unit_id.toString(),
      businessUnitName: entry.business_unit_name,
      businessUnitIdentifier: entry.business_unit_identifier,
      isBillable: entry.is_billable === 1,
      hours: entry.hours.toString().padStart(2, '0'),
      minutes: entry.minutes.toString().padStart(2, '0'),
      workCompleted: entry.work_completed,
      project: entry.project || '',
    });
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle entry delete
  const handleDelete = async (entryId) => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/mytime/${entryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete time entry');
      }
      
      // Refresh data
      const entriesResponse = await fetch(`${API_URL}/mytime?employee_id=${currentUser.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (entriesResponse.ok) {
        const entriesData = await entriesResponse.json();
        setTimeEntries(entriesData.entries);
      }
      
      // Refresh summaries
      await fetchDailySummary();
      await fetchBusinessUnitSummary();
      
      setSuccess('Time entry deleted successfully');
      setShowDeleteConfirm(null);
      
      // If editing entry was deleted, reset form
      if (editingEntry && editingEntry.id === entryId) {
        setEditingEntry(null);
        setFormData({
          businessUnit: '',
          businessUnitName: '',
          businessUnitIdentifier: '',
          isBillable: false,
          hours: '00',
          minutes: '00',
          workCompleted: '',
          project: '',
        });
      }
    } catch (err) {
      setError('Failed to delete time entry. Please try again.');
      console.error('Error deleting time entry:', err);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display in user's timezone
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      console.log('formatDate input:', dateString); // Debug log
      
      // Always extract just the date part for calendar date display
      let dateKey = dateString;
      if (dateKey.includes('T')) {
        dateKey = dateKey.split('T')[0]; // Extract yyyy-mm-dd part
      }
      
      console.log('dateKey after extraction:', dateKey); // Debug log
      
      // Parse as local date to avoid timezone shifts for calendar dates
      const [year, month, day] = dateKey.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date string:', dateString);
        return dateString; // Return original if can't parse
      }
      
      // Format directly without timezone conversion since this is a calendar date
      const result = dateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
      
      console.log('formatDate result:', result); // Debug log
      return result;
      
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return dateString; // Fallback to original string
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingEntry(null);
    setFormData({
      businessUnit: '',
      businessUnitName: '',
      businessUnitIdentifier: '',
      isBillable: false,
      hours: '00',
      minutes: '00',
      workCompleted: '',
      project: '',
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main content area */}
      <div className="lg:col-span-2">
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-semibold text-slate-200 flex items-center">
              <Clock className="w-5 h-5 mr-2" /> 
              {editingEntry ? 'Edit Time Entry' : 'Log Your Time'}
            </h2>
          </div>
          
          {/* Error and Success Messages */}
          {error && (
            <div className="mx-6 mt-4 bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
              <button 
                onClick={() => setError(null)} 
                className="ml-auto text-red-300 hover:text-red-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {success && (
            <div className="mx-6 mt-4 bg-green-900/30 border border-green-800 text-green-300 px-4 py-3 rounded-md flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>{success}</div>
              <button 
                onClick={() => setSuccess(null)} 
                className="ml-auto text-green-300 hover:text-green-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Entry Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Business Unit*
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <select
                    name="businessUnit"
                    value={formData.businessUnit}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    required
                  >
                    <option value="">Select Business Unit</option>
                    {businessUnits.map(unit => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name} - {unit.identifier}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center h-full pt-7">
                <div className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    name="isBillable"
                    id="isBillable"
                    checked={formData.isBillable}
                    onChange={handleInputChange}
                    className="form-checkbox h-5 w-5 text-blue-600 bg-slate-700 border-slate-600 
                            rounded focus:ring-blue-500 focus:ring-offset-slate-800"
                  />
                  <label htmlFor="isBillable" className="ml-2 text-sm text-slate-300 flex items-center">
                    <DollarSign className="w-4 h-4 mr-1 text-green-400" />
                    Billable?
                  </label>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Project
                </label>
                <div className="relative">
                  <select
                    name="project"
                    value={formData.project}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  >
                    <option value="">Select Project (Optional)</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.name}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Time Spent*
                </label>
                <div className="flex space-x-4">
                  <div className="flex-1 relative">
                    <label className="absolute -top-5 left-0 text-xs text-slate-400">Hours</label>
                    <select
                      name="hours"
                      value={formData.hours}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    >
                      {hoursOptions.map(hour => (
                        <option key={`hour-${hour}`} value={hour}>
                          {hour}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="flex-1 relative">
                    <label className="absolute -top-5 left-0 text-xs text-slate-400">Minutes</label>
                    <select
                      name="minutes"
                      value={formData.minutes}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    >
                      {minutesOptions.map(minute => (
                        <option key={`min-${minute}`} value={minute}>
                          {minute}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Work Completed*
              </label>
              <div className="relative">
                <File className="absolute left-3 top-3 text-slate-400 w-5 h-5" />
                <textarea
                  name="workCompleted"
                  value={formData.workCompleted}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe the work you completed..."
                  required
                ></textarea>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              {editingEntry && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="flex items-center px-4 py-2 border border-slate-600 text-slate-300 
                           rounded-md hover:bg-slate-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 
                         text-white rounded-md transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : editingEntry ? (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Entry
                  </>
                ) : (
                  <>
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Log Time
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        
        {/* Past Entries */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-semibold text-slate-200 flex items-center">
              <Calendar className="w-5 h-5 mr-2" /> 
              Recent Time Entries
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Showing your time entries for the last 30 days
            </p>
          </div>
          
          <div className="p-6">
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            )}
            
            {!loading && timeEntries.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No time entries found for the last 30 days.</p>
                <p className="text-sm mt-1">Log your first time entry above.</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {timeEntries.map(entry => (
                  <div 
                    key={entry.id} 
                    className="bg-slate-700/30 border border-slate-600 rounded-md p-4"
                  >
                    <div className="flex justify-between">
                      <div className="flex items-center mb-2">
                        <span className="text-slate-200 font-medium">
                          {formatDate(entry.date)}
                        </span>
                        <span className="mx-2 text-slate-500">•</span>
                        <span className="text-blue-400 text-sm font-medium">
                          {entry.business_unit_name} - {entry.business_unit_identifier}
                        </span>
                        {entry.is_billable === 1 && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/30 text-green-300 border border-green-800/50">
                            <DollarSign className="w-3 h-3 mr-1" />
                            Billable
                          </span>
                        )}
                      </div>
                      
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEdit(entry)}
                          className="p-1 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                          title="Edit entry"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => setShowDeleteConfirm(entry.id)}
                          className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-600/30 rounded"
                          title="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="text-slate-300 text-sm mb-2">
                      <span className="font-medium">{entry.hours}h {entry.minutes}m</span>
                      {entry.project && (
                        <span className="ml-2">
                          {entry.project}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-slate-400 text-sm mb-2">
                      {entry.work_completed}
                    </p>
                    
                    {/* Timestamps */}
                    {(entry.created_at || entry.updated_at) && (
                      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 border-t border-slate-600/30 pt-2">
                        {entry.created_at && (
                          <div className="flex items-center">
                            <User className="w-3 h-3 mr-1" />
                            <span>Created: {formatInTimezone(entry.created_at, userTimezone, { 
                              month: 'short', 
                              day: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit',
                              timeZoneName: 'short'
                            })}</span>
                          </div>
                        )}
                        {entry.updated_at && entry.updated_at !== entry.created_at && (
                          <div className="flex items-center">
                            <Edit2 className="w-3 h-3 mr-1" />
                            <span>Updated: {formatInTimezone(entry.updated_at, userTimezone, { 
                              month: 'short', 
                              day: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit',
                              timeZoneName: 'short'
                            })}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Delete confirmation */}
                    {showDeleteConfirm === entry.id && (
                      <div className="mt-3 pt-3 border-t border-slate-600/50">
                        <p className="text-red-300 text-sm mb-2">
                          Are you sure you want to delete this time entry?
                        </p>
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="px-3 py-1 text-sm text-slate-300 hover:text-slate-100"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleDelete(entry.id)}
                            className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Summary Panel */}
      <div className="lg:col-span-1">
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-slate-200">Weekly Summary</h2>
            <p className="text-sm text-slate-400 mt-1">Last 7 days</p>
          </div>
          
          <div className="p-6">
            <h3 className="text-md font-medium text-slate-300 mb-3">Daily Hours</h3>
            
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : dailySummary.length > 0 ? (
              <div className="space-y-3">
                {dailySummary.map((day, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-sm text-slate-300">
                      {(() => {
                        try {
                          if (!day.date || isNaN(day.date.getTime())) return 'Unknown Date';
                          return formatInTimezone(day.date, userTimezone, { weekday: 'short', month: 'short', day: 'numeric', timeZoneName: undefined });
                        } catch (error) {
                          console.warn('Error formatting daily summary date:', day.date, error);
                          return 'Date Error';
                        }
                      })()}
                    </span>
                    <div className="flex items-center">
                      <div className="w-32 bg-slate-700 rounded-full h-2.5 mr-2">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, ((day.totalHours + (day.totalMinutes / 60)) / 8) * 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-slate-300 w-16 text-right">
                        {day.formattedTotal}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">No time entries found for the last 7 days.</p>
            )}
          </div>
        </div>
        
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-slate-200">Top Business Units</h2>
            <p className="text-sm text-slate-400 mt-1">Your most logged units</p>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : topBusinessUnits.length > 0 ? (
              <div className="space-y-5">
                {topBusinessUnits.map((bu, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-slate-300">
                        {bu.name} - {bu.identifier}
                      </span>
                      <span className="text-sm text-slate-400">
                        {bu.formattedTotal}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full ${index === 0 ? 'bg-blue-600' : index === 1 ? 'bg-green-500' : 'bg-purple-500'}`} 
                        style={{ width: `${Math.min(100, (bu.totalMinutes / (topBusinessUnits[0]?.totalMinutes || 1)) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">No time entries found for the last 7 days.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyTime;
